
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { commonModule, installationsModule, companyModule, tasksModule, appointmentsModule, statsModule, jobsModule, clientsModule, dashboardModule, filterModule, itemsModule, paymentsModule, offersModule } from '@/store/modules/store-accessor'
import LoadingIndicator from '@/components/TheLoadingIndicator.vue'
import InstallationsSideviewGeneralTab from '@/components/sideviews/installations/InstallationsSideviewGeneralTab.vue'
import InstallationsSideviewJobsTab from '@/components/sideviews/installations/InstallationsSideviewJobsTab.vue'
import InstallationsSideviewAppointmentsTab from '@/components/sideviews/installations/InstallationsSideviewAppointmentsTab.vue'
import InstallationsSideviewItemsTab from '@/components/sideviews/installations/InstallationsSideviewItemsTab.vue'
import InstallationsSideviewPhotosTab from '@/components/sideviews/installations/InstallationsSideviewPhotosTab.vue'
import InstallationsSideviewTasksTab from '@/components/sideviews/installations/InstallationsSideviewTasksTab.vue'
import InstallationsSideviewFilesTab from '@/components/sideviews/installations/InstallationsSideviewFilesTab.vue'
import InstallationsSideviewOffersTab from './installations/InstallationSideviewOffersTab.vue'
import { TimeUtil } from '@/helpers/TimeUtil'
import FileSaver from 'file-saver';
import dayjs from 'dayjs';
import TheMiniLoader from '@/components/TheMiniLoader.vue'
import TaskModule from '@/store/modules/tasks-module'
import { formatAmount, templatesFilenameLanguage } from "@/helpers/data";
import { resolveSideviewTabName } from '@/helpers/installationSideviewTab';

@Component({
  components: {
    LoadingIndicator,
    InstallationsSideviewGeneralTab,
    InstallationsSideviewJobsTab,
    InstallationsSideviewAppointmentsTab,
    InstallationsSideviewItemsTab,
    InstallationsSideviewPhotosTab,
    InstallationsSideviewTasksTab,
    InstallationsSideviewFilesTab,
    InstallationsSideviewOffersTab,
    TheMiniLoader
  }
})
export default class InstallationsSideview extends Vue {
  @Prop()
  loading!: boolean;

  @Prop()
  payload!: any;
  
  currTab = this.mapTabs();
  offerId = this.payload['offerId'] || 0;
  jobId = this.payload['jobId'] || 0;
  appointmentId = this.payload['appointmentId'] || 0;
  recurrenceId = this.payload['recurrenceId'] || 0;
  taskId = this.payload['taskId'] || 0;
  loadedTabs = new Set<string>();

  isEditing!: boolean;

  mounted() {
    if (installationsModule.preparedSideviewTabs.length > 0) {
      installationsModule.preparedSideviewTabs.forEach((tab) => this.loadedTabs.add(tab));
    } else {
      void Promise.all([
        installationsModule.getInstallationDetailsInfo(this.currInstallationId),
        installationsModule.getInstallationDetailsFinancials(this.currInstallationId),
        installationsModule.getInstallationDetailsStats(this.currInstallationId),
      ]).then(() => {
        this.loadedTabs.add('general');
      });
    }

    if (!this.loadedTabs.has(this.currTab)) {
      this.makeTabCall(this.currTab);
    }
  }

  get offersLength() {
    return this.currInstallation?.stats?.offers_total;
  }

  @Watch('currTab')
  onTabChange(newTab: number | string) {
    this.makeTabCall(newTab);
  }

  async hideSideview () {
    commonModule.hideSideview();
    installationsModule.setInstallationOffers([]);
    installationsModule.setInstallationOfferDetails(null);
  }

  get currInstallationId () {
    return this.payload['installationId'];
  }

  get currInstallation () {
    return installationsModule.currentInstallation;
  }

  get exportPDFEnabled () {
    //TODO Ths is quick and dirty, we need to change this and implement it thourgh feature flags
    return companyModule.company.key == 'GSOP' ? true : false
  }

  get jobsRatio() {
    return this.currInstallation?.stats?.jobs_ratio;
  }

  get appointmentsRatio() {
    return this.currInstallation?.stats?.appointments_ratio;
  }

  get tasksRatio() {
    return this.currInstallation?.stats?.tasks_ratio;
  }

  get itemsTotal() {
    return this.currInstallation?.stats?.items_total;
  }

  get photosTotal() {
    return this.currInstallation?.stats?.images_total;
  }

  get filesTotal() {
    return this.currInstallation?.stats?.files_total;
  }

  async installationPDF() {
      const doc = await installationsModule.getInstallationPDF(this.currInstallationId);
      var file = new File([doc], `${templatesFilenameLanguage()}_${this.currInstallation.info.tag_id}_${dayjs().format('DD_MM_YYYY')}.pdf`);
      FileSaver.saveAs(file);
  }

  formatDate (date: Date): string {
    return TimeUtil.formatDateTime(date)
  }

  formatBalance(balance: number) {
    return balance ? formatAmount(balance) : '0';
  }

  updated() {
    this.isEditing = false;
  }

  get isLoading() {
    return installationsModule.isLoadingSideView;
  }

  get isPremium () {
    return installationsModule?.currentInstallation?.info?.is_premium;
  }

  get tagId () {
    return installationsModule?.currentInstallation?.info?.tag_id?.split("_").pop();
  }

  async refreshData () {
    const offersTab = this.$refs.offersTab as InstallationsSideviewOffersTab | undefined;
    const expandedOfferId = this.currTab === 'offers' ? offersTab?.getExpandedOfferId() : null;

    this.loadedTabs.clear(); // Clears / Refreshes all of the visited tabs

    installationsModule.setIsLoadingSideView(true);
    await Promise.all([
      installationsModule.getInstallationDetailsFinancials(this.currInstallationId),
      installationsModule.getInstallationDetailsStats(this.currInstallationId),
      companyModule.getCompanyInfo(),
      this.makeTabCall(this.currTab, true),
    ]);

    offersTab?.refreshOfferExpansion(expandedOfferId);
  }

  async makeTabCall(tab: number | string, forceRefresh = false) {
    const tabName = resolveSideviewTabName(tab);
    const tabIsInvalidated = installationsModule.invalidatedInstallationTabs.includes(tabName);

    if (!tabName || (!forceRefresh && !tabIsInvalidated && this.loadedTabs.has(tabName))) {
      return;
    }

    const apiCalls: Record<string, any> = {
      general: installationsModule.getInstallationDetailsInfo,
      offers: installationsModule.getInstallationOffers,
      jobs: installationsModule.getInstallationDetailsJobs,
      appointments: installationsModule.getInstallationDetailsAppointments,
      tasks: installationsModule.getInstallationDetailsTasks,
      items: installationsModule.getInstallationDetailsItems,
      photos: installationsModule.getInstallationDetailsPhotos,
      files: installationsModule.getInstallationDetailsFiles,
    };

    const apiCall = apiCalls[tabName];

    if (!apiCall) {
      return;
    }

    this.loadedTabs.add(tabName);

    try {
      await apiCall(this.currInstallationId);
      installationsModule.validateInstallationTab(tabName);
    } catch (error) {
      this.loadedTabs.delete(tabName);
      throw error;
    }
  }

  async editInstallation() {
     await installationsModule.getInstallationDetailsInfo(this.currInstallationId);
    commonModule.showModal({ name: 'edit-installation-modal', payload: { view: "installationsSideview"}})
  }

  deleteInstallation() {
    commonModule.showModal({ name: 'delete-installation-modal', payload: { installation: this.currInstallation.info}})
  }

  close () {
    installationsModule.unsetCurrentInstallation();

    switch(this.$route.name) {
      case 'Dashboard':
        dashboardModule.getDashboardAppointmentsAction(dashboardModule.filters);
        dashboardModule.getDashboardAppointmentsBarsAction(dashboardModule.filters);
        filterModule.getDashboardTasksFilters();
        dashboardModule.getDashboardPendingTasks(dashboardModule.taskFilters);
        break;
      case 'Installations':
        installationsModule.getInstallationsAction(installationsModule.filters);
        break;
      case 'Jobs':
        jobsModule.getJobs(jobsModule.filters);
        jobsModule.getJobTotals(jobsModule.filters);
        break;
      case 'offers':
        offersModule.getAllOffers(this.offersQuery());
        break;
      case 'Map':
        installationsModule.getInstallationsAction(installationsModule.filters);
        break;
      case 'Calendar':
        appointmentsModule.getCalendarAppointmentsAction(appointmentsModule.filters);
        break;
      case 'Appointments':
        filterModule.getAppointmentsFilters();
        appointmentsModule.getAppointmentsAction(appointmentsModule.appointmentsFilters);
        break;
      case 'Tasks':
        tasksModule.getTasks(tasksModule.filters);
        break;
      case 'My Company':
        paymentsModule.getPaymentsAction(paymentsModule.filters);
        break;
    }
    this.hideSideview()
  }

  offersQuery() {
    const params = new URLSearchParams();

    Object.keys(this.$route.query).forEach((key) => {
      const value = this.$route.query[key];

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            params.append(key, item);
          }
        });
      } else if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });

    params.set('page', (offersModule.offersPagination.page || 1).toString());
    params.set('limit', (offersModule.offersPagination.limit || 20).toString());

    const query = params.toString();
    return query ? `?${query}` : '';
  }

  get companyHasAdvancedPlan() {
    return companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN');
  }

  mapTabs() {
    const tab = this.payload['tab'];
    const companyModules = companyModule.company?.modules;

    if (tab === 'offers' && companyModules !== undefined && !companyModule.company?.modules?.includes('DFB_ADVANCED_PLAN')) {
      return 'general';
    }

    return resolveSideviewTabName(tab);
  }

};
